import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { apiErrorMessage } from '@/shared/api-client/workspace';
import { useCreateRoom, useRooms, useSetRoomActive, useUpdateRoom, type Room } from './rooms';
import type { Venue } from './venues';

/** Maps a backend error code to a translated, user-facing message. */
export function RoomsDialog({
  venue,
  onOpenChange,
}: {
  venue: Venue | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('common');

  const [editing, setEditing] = useState<Room | 'new' | null>(null);

  useEffect(() => {
    if (!venue) setEditing(null);
  }, [venue]);

  if (!venue) return null;

  return (
    <Dialog open={venue !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('venues.rooms.title', { name: venue.name })}</DialogTitle>
          <DialogDescription>{t('venues.rooms.description')}</DialogDescription>
        </DialogHeader>

        <RoomsManager
          venueId={venue.id}
          editing={editing}
          onEditingChange={setEditing}
          showFooter
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

export function RoomsPage() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const [editing, setEditing] = useState<Room | 'new' | null>(null);
  const venueName = venues.find((venue) => venue.id === venueId)?.name ?? venueId;

  return (
    <section className="grid gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('venues.rooms.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('venues.rooms.scopedDescription', { name: venueName })}
        </p>
      </div>

      <RoomsManager venueId={venueId} editing={editing} onEditingChange={setEditing} />
    </section>
  );
}

function RoomsManager({
  venueId,
  editing,
  onEditingChange,
  showFooter = false,
  onClose,
}: {
  venueId: string;
  editing: Room | 'new' | null;
  onEditingChange: (editing: Room | 'new' | null) => void;
  showFooter?: boolean;
  onClose?: () => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canManage = auth.can('venue.room.manage', { kind: 'venue', venueId });

  const { data, isLoading, isError } = useRooms(venueId);
  const setActive = useSetRoomActive(venueId);

  async function toggleActive(room: Room) {
    try {
      await setActive.mutateAsync({ roomId: room.id, active: !room.active });
      toast.success(
        t(room.active ? 'venues.rooms.toast.deactivated' : 'venues.rooms.toast.activated'),
      );
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'venues.rooms', caught));
    }
  }

  if (editing !== null) {
    return (
      <RoomForm
        venueId={venueId}
        room={editing === 'new' ? null : editing}
        onDone={() => onEditingChange(null)}
      />
    );
  }

  return (
    <>
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => onEditingChange('new')}>
            {t('venues.rooms.actions.create')}
          </Button>
        </div>
      )}
      {isLoading && <p className="text-muted-foreground">{t('venues.rooms.loading')}</p>}
      {isError && <p className="text-destructive">{t('venues.rooms.errors.loadFailed')}</p>}
      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('venues.rooms.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('venues.rooms.columns.capacity')}</th>
                <th className="px-4 py-2 font-medium">{t('venues.rooms.columns.status')}</th>
                {canManage && (
                  <th className="px-4 py-2 font-medium">{t('venues.rooms.columns.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td
                    colSpan={canManage ? 4 : 3}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {t('venues.rooms.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((room) => (
                <tr key={room.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{room.name}</td>
                  <td className="px-4 py-2">{room.capacity}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {t(room.active ? 'venues.rooms.status.active' : 'venues.rooms.status.inactive')}
                  </td>
                  {canManage && (
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => onEditingChange(room)}>
                          {t('venues.rooms.actions.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={setActive.isPending}
                          onClick={() => toggleActive(room)}
                        >
                          {t(
                            room.active
                              ? 'venues.rooms.actions.deactivate'
                              : 'venues.rooms.actions.activate',
                          )}
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showFooter && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

function RoomForm({
  venueId,
  room,
  onDone,
}: {
  venueId: string;
  room: Room | null;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const create = useCreateRoom(venueId);
  const update = useUpdateRoom(venueId);
  const [name, setName] = useState(room?.name ?? '');
  const [capacity, setCapacity] = useState(String(room?.capacity ?? 1));
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const parsed = Number(capacity);
    if (!Number.isInteger(parsed) || parsed < 1) {
      setError(t('venues.rooms.errors.invalid_capacity'));
      return;
    }
    try {
      if (room) {
        await update.mutateAsync({ roomId: room.id, input: { name, capacity: parsed } });
        toast.success(t('venues.rooms.toast.updated'));
      } else {
        await create.mutateAsync({ name, capacity: parsed });
        toast.success(t('venues.rooms.toast.created'));
      }
      onDone();
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues.rooms', caught));
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="room-name">{t('venues.rooms.fields.name')}</Label>
        <Input
          id="room-name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="room-capacity">{t('venues.rooms.fields.capacity')}</Label>
        <Input
          id="room-capacity"
          type="number"
          required
          value={capacity}
          onChange={(event) => setCapacity(event.target.value)}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {pending
            ? t('venues.rooms.form.submitting')
            : room
              ? t('venues.rooms.form.save')
              : t('venues.rooms.form.create')}
        </Button>
      </DialogFooter>
    </form>
  );
}
